import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';

const CallLogPanel = ({ callLogs, onCallLogUpdate, userId, onContactSelect }) => {
  const [selectedCallLog, setSelectedCallLog] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOutcome, setFilterOutcome] = useState('all');

  const filteredCallLogs = callLogs?.filter(log => {
    const matchesSearch = !searchQuery || 
      log?.contact?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      log?.call_notes?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      log?.phone_number?.includes(searchQuery);
    
    const matchesFilter = filterOutcome === 'all' || log?.outcome === filterOutcome;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Call Logs</h2>
        <Button
          onClick={() => {
            setSelectedCallLog(null);
            setShowEditModal(true);
          }}
          iconName="Plus"
          iconPosition="left"
          size="sm"
        >
          Log Call
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by contact, notes, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>
        <select
          value={filterOutcome}
          onChange={(e) => setFilterOutcome(e?.target?.value)}
          className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
        >
          <option value="all">All Outcomes</option>
          <option value="successful">Successful</option>
          <option value="interested">Interested</option>
          <option value="callback_requested">Callback Requested</option>
          <option value="no_answer">No Answer</option>
          <option value="not_interested">Not Interested</option>
        </select>
      </div>

      {/* Call Logs List */}
      {loading ? (
        <div className="text-center py-8">
          <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading call logs...</p>
        </div>
      ) : filteredCallLogs?.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Icon name="Phone" size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">
            {searchQuery || filterOutcome !== 'all' ? 'No call logs match your filters' : 'No call logs yet'}
          </p>
          <p className="text-sm mt-2">
            {searchQuery || filterOutcome !== 'all' ? 'Try adjusting your search or filters' : 'Your call logs will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredCallLogs?.map(log => (
            <div
              key={log?.id}
              className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${log?.outcome === 'successful' ? 'bg-success/10' : 
                      log?.outcome === 'interested' ? 'bg-primary/10' :
                      log?.outcome === 'callback_requested'? 'bg-warning/10' : 'bg-muted/50'
                    }
                  `}>
                    <Icon 
                      name={log?.direction === 'inbound' ? 'PhoneIncoming' : 'PhoneOutgoing'} 
                      size={20} 
                      className={
                        log?.outcome === 'successful' ? 'text-success' : 
                        log?.outcome === 'interested' ? 'text-primary' :
                        log?.outcome === 'callback_requested'? 'text-warning' : 'text-muted-foreground'
                      }
                    />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-foreground">
                        {log?.contact?.name || 'Unknown Contact'}
                      </h3>
                      {log?.contact && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onContactSelect?.(log?.contact)}
                          iconName="User"
                          className="h-6 w-6 p-0 text-primary"
                        />
                      )}
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>{log?.phone_number}</span>
                      <span>•</span>
                      <span className="capitalize">{log?.direction}</span>
                      {log?.duration_seconds > 0 && (
                        <>
                          <span>•</span>
                          <span>{Math.floor(log?.duration_seconds / 60)}m {log?.duration_seconds % 60}s</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {log?.outcome && (
                    <span className={`
                      text-xs px-2 py-1 rounded-full font-medium
                      ${log?.outcome === 'successful' ? 'bg-success/10 text-success' : 
                        log?.outcome === 'interested' ? 'bg-primary/10 text-primary' :
                        log?.outcome === 'callback_requested' ? 'bg-warning/10 text-warning' :
                        log?.outcome === 'no_answer' ? 'bg-muted text-muted-foreground' :
                        log?.outcome === 'not_interested'? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'
                      }
                    `}>
                      {log?.outcome?.replace('_', ' ')}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(log?.call_started_at || log?.created_at), 'MMM dd, HH:mm')}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedCallLog(log);
                      setShowEditModal(true);
                    }}
                    iconName="Edit"
                    className="h-8 w-8 p-0"
                  />
                </div>
              </div>

              {log?.call_notes && (
                <div className="bg-muted/30 rounded-lg p-3 mt-3">
                  <div className="flex items-start space-x-2">
                    <Icon name="MessageSquare" size={14} className="text-primary mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-foreground font-medium mb-1">Notes:</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {log?.call_notes}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {log?.follow_up_scheduled && (
                <div className="mt-3 flex items-center space-x-2 text-sm text-primary">
                  <Icon name="Calendar" size={14} />
                  <span>Follow-up scheduled</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit Call Log Modal */}
      {showEditModal && selectedCallLog && (
        <EditCallLogModal
          callLog={selectedCallLog}
          onSave={(updatedLog) => {
            setShowEditModal(false);
            setSelectedCallLog(null);
            onCallLogUpdate?.();
          }}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedCallLog(null);
          }}
          userId={userId}
        />
      )}
    </div>
  );
};

// Edit Call Log Modal Component
const EditCallLogModal = ({ callLog, onSave, onCancel, userId }) => {
  const [formData, setFormData] = useState({
    call_notes: callLog?.call_notes || '',
    outcome: callLog?.outcome || '',
    follow_up_scheduled: callLog?.follow_up_scheduled || false,
    duration_seconds: callLog?.duration_seconds || 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: updateError } = await supabase
        ?.from('call_logs')
        ?.update(formData)
        ?.eq('id', callLog?.id)
        ?.select()
        ?.single();

      if (updateError) {
        setError(updateError?.message);
        return;
      }

      onSave?.(data);
    } catch (err) {
      setError('Failed to update call log');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Edit Call Log</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            iconName="X"
          />
        </div>

        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={14} className="text-destructive" />
                <span className="text-destructive text-sm">{error}</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Call Notes
            </label>
            <textarea
              value={formData?.call_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, call_notes: e?.target?.value }))}
              placeholder="Enter notes about this call..."
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Outcome
            </label>
            <select
              value={formData?.outcome}
              onChange={(e) => setFormData(prev => ({ ...prev, outcome: e?.target?.value }))}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="">Select outcome</option>
              <option value="successful">Successful</option>
              <option value="interested">Interested</option>
              <option value="callback_requested">Callback Requested</option>
              <option value="no_answer">No Answer</option>
              <option value="not_interested">Not Interested</option>
              <option value="busy">Busy</option>
              <option value="voicemail">Voicemail</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="follow-up"
              checked={formData?.follow_up_scheduled}
              onChange={(e) => setFormData(prev => ({ ...prev, follow_up_scheduled: e?.target?.checked }))}
              className="w-4 h-4 text-primary bg-background border-border rounded"
            />
            <label htmlFor="follow-up" className="text-sm font-medium text-foreground">
              Follow-up scheduled
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            iconName={loading ? "Loader2" : "Save"}
            iconPosition="left"
            className={loading ? "animate-spin" : ""}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallLogPanel;